import React, { useState } from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";
import { Copy } from "lucide-react";
import { useSelector } from "react-redux";

import messages from "../../../../../../assets/locale/messages";
import { clx } from "../../../../../../utils/Helpers";
import { isPaidTrainingToggleOn } from "../../../../../../utils/paidTrainingRemoval";
import { getShiftOptionLabel } from "../helpers";

import "./DuplicateTrainingFromShift.scss";

/** Copy of a shift's training without its persisted identity, so it saves (and validates) as new. */
export const toDuplicatedTraining = (training) =>
  Object.fromEntries(
    Object.entries(training).filter(([key]) => key !== "id" && key !== "_destroy"),
  );

const DuplicateTrainingFromShift = ({
  formik,
  roleIndex,
  shiftIndex,
  shifts,
  disabled,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events, shared } = messages[lang];
  const t = events?.creatEvent?.defineRolesAndShifts;
  const [selectedSourceByShift, setSelectedSourceByShift] = useState({});

  const sources = (shifts || [])
    .map((shift, index) => ({ shift, index }))
    .filter(
      ({ shift, index }) =>
        index !== shiftIndex && isPaidTrainingToggleOn(shift?.training_attributes),
    );

  if (sources.length === 0) return null;

  const selectionKey = `${roleIndex}-${shiftIndex}`;
  const selectedSource = selectedSourceByShift[selectionKey];

  const handleDuplicate = (sourceIndex) => {
    const sourceTraining = shifts[sourceIndex]?.training_attributes;
    if (!sourceTraining) return;

    const trainingPath = `event_roles_attributes.${roleIndex}.event_role_shifts_attributes.${shiftIndex}.training_attributes`;

    // setFieldValue validates against the new values; touching without a second
    // validation pass avoids a stale-values race (see PROLINE-2531).
    formik.setFieldValue(trainingPath, toDuplicatedTraining(sourceTraining));
    formik.setFieldTouched(`${trainingPath}.starts_at`, true, false);
    formik.setFieldTouched(`${trainingPath}.ends_at`, true, false);

    setSelectedSourceByShift((prev) => ({ ...prev, [selectionKey]: sourceIndex }));
  };

  return (
    <div className="duplicate-training">
      <div className="duplicate-training__header">
        <span className="duplicate-training__icon" aria-hidden="true">
          <Copy size={20} />
        </span>
        <div className="duplicate-training__text">
          <Typography className="duplicate-training__title text-heading-sm" component="span">
            {t.duplicateTrainingTitle}
          </Typography>
          <Typography className="duplicate-training__description text-label-md" component="span">
            {t.duplicateTrainingDescription}
          </Typography>
        </div>
      </div>

      <div className="duplicate-training__sources">
        <Typography
          className="duplicate-training__from text-label-md"
          component="span"
          id={`duplicate-training-from-${selectionKey}`}
        >
          {t.duplicateTrainingFrom}
        </Typography>
        <div
          className="duplicate-training__buttons"
          role="group"
          aria-labelledby={`duplicate-training-from-${selectionKey}`}
        >
          {sources.map(({ shift, index }) => (
            <button
              key={index}
              type="button"
              className={clx("duplicate-training__source", {
                "duplicate-training__source--selected": selectedSource === index,
              })}
              aria-pressed={selectedSource === index}
              disabled={disabled}
              onClick={() => handleDuplicate(index)}
            >
              {getShiftOptionLabel(shared, shift.shift_type) ||
                t.shiftLabel.replace("{number}", index + 1)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

DuplicateTrainingFromShift.propTypes = {
  formik: PropTypes.object.isRequired,
  roleIndex: PropTypes.number.isRequired,
  shiftIndex: PropTypes.number.isRequired,
  shifts: PropTypes.array,
  disabled: PropTypes.bool,
};

DuplicateTrainingFromShift.defaultProps = {
  shifts: [],
  disabled: false,
};

export default DuplicateTrainingFromShift;
